/**
 * A proposal as the customer reads it.
 *
 * One component for the hosted page at `/p/:token` and for the builder's
 * "Preview as customer", so what the office previews is what the customer
 * gets. It draws; it does not fetch and it does not accept. The choice of
 * package and add-ons is the caller's state, and without `onChoice` the page
 * is read-only — the signed copy, or a preview of one.
 *
 * It is drawn on the business's letterhead, in its colours and in Open Sans,
 * the way the PDF is (decision 81). Every figure comes from `proposalTotals`,
 * the function the server prices an acceptance with. Decision 80.
 */
import {
  availableAddOns,
  letterDate,
  proposalBlocks,
  proposalTotals,
  unitPriceIncTax,
  type ProposalChoice,
} from '@fieldops/shared'
import { Fragment, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { formatDate, money } from '../../lib/format.js'
import type { RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { CoverSheet, type CoverDetail } from './Cover.js'
import { Letterhead, themeOf, themeVars } from './Letterhead.js'

export type PortalView = RouterOutputs['portal']['view']
export type ProposalView = PortalView['proposal']
export type BusinessView = PortalView['business']
export type ProposalLineView = ProposalView['lines'][number]
export type ProposalFileView = ProposalView['sections'][number]['files'][number]

export interface Choice {
  optionId: string | null
  addOnIds: string[]
}

export function totalsFor(proposal: ProposalView, choice: ProposalChoice) {
  return proposalTotals(
    {
      lines: proposal.lines,
      options: proposal.options,
      discountAmount: proposal.discountAmount,
    },
    choice,
  )
}

/** Where the price goes when nobody placed it: before the terms, else last. */
function withPricing(sections: ProposalView['sections']) {
  if (sections.some((section) => section.kind === 'PRICING')) return sections

  const pricing = {
    id: '__pricing',
    kind: 'PRICING' as const,
    title: null,
    body: null,
    imageUrl: null,
    imageWidth: null,
    imageHeight: null,
    files: [],
  }
  const terms = sections.findIndex((section) => section.kind === 'TERMS')
  if (terms === -1) return [...sections, pricing]
  return [...sections.slice(0, terms), pricing, ...sections.slice(terms)]
}

export function ProposalPage({
  business,
  proposal,
  choice,
  onChoice,
  children,
  className,
}: {
  business: BusinessView
  proposal: ProposalView
  choice: Choice
  /** Absent: the page shows the choice and does not let anybody change it. */
  onChoice?: (choice: Choice) => void
  /** Accept, decline and download, drawn on the body sheet under the words. */
  children?: ReactNode
  className?: string
}) {
  const [first, ...rest] = proposal.sections
  const cover = first?.kind === 'COVER' ? first : null
  const body = withPricing(cover ? rest : proposal.sections)
  const hasTermsSection = body.some((section) => section.kind === 'TERMS')
  const theme = themeOf(business)
  const number = `${proposal.number}${proposal.version > 1 ? ` · v${proposal.version}` : ''}`

  return (
    <div
      style={themeVars(theme)}
      className={cn('mx-auto flex w-full max-w-3xl flex-col gap-4 font-proposal text-[15px] text-ink-800', className)}
    >
      {cover ? (
        <CoverSheet
          business={business}
          number={number}
          date={letterDate(proposal.date)}
          photo={cover.imageUrl ? <img src={cover.imageUrl} alt="" className="aspect-[16/7] w-full object-cover" /> : null}
          title={
            <h1 className="text-3xl leading-tight font-extrabold text-[var(--p-brand-text)] sm:text-5xl">
              {cover.title || proposal.title || 'Your proposal'}
            </h1>
          }
          intro={cover.body ? <Prose body={cover.body} className="text-base text-ink-700 sm:text-lg" /> : null}
          details={coverDetails(proposal)}
          className="rounded-none ring-0 sm:rounded-2xl sm:shadow-sm sm:ring-1 sm:ring-ink-200"
        />
      ) : null}

      <article className="overflow-hidden rounded-none bg-white sm:rounded-2xl sm:shadow-sm sm:ring-1 sm:ring-ink-200">
        <div aria-hidden className="h-1.5 bg-[var(--p-brand)]" />
        <div className="px-5 py-6 sm:px-10 sm:py-10">
          {cover ? null : (
            <>
              <Letterhead business={business} number={number} date={letterDate(proposal.date)} />
              <section aria-label="Proposal" className="pt-6 pb-4">
                <h1 className="text-2xl leading-tight font-bold text-[var(--p-brand)] sm:text-[28px]">
                  {proposal.title || 'Your proposal'}
                </h1>

                <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl bg-[var(--p-brand-soft)]/60 px-5 py-4 text-sm ring-1 ring-[var(--p-brand-soft)] sm:grid-cols-4">
                  <Fact label="Prepared for" value={proposal.customerName} />
                  <Fact label="Property" value={proposal.siteAddress} />
                  <Fact label="Job" value={proposal.jobNumber} />
                  <Fact label="Valid until" value={proposal.validUntil ? formatDate(proposal.validUntil) : null} />
                </dl>
              </section>
            </>
          )}

          {body.map((section) => (
            <Fragment key={section.id}>
              {section.kind === 'PRICING' ? (
                <ProposalPricing
                  title={section.title}
                  body={section.body}
                  proposal={proposal}
                  choice={choice}
                  onChoice={onChoice}
                />
              ) : section.kind === 'IMAGE' ? (
                <section aria-label={section.title ?? 'Photos and files'} className="py-5">
                  {section.title && <h2 className="text-base font-bold text-ink-900">{section.title}</h2>}
                  <ProposalFiles files={section.files} className={section.title ? 'mt-3' : undefined} />
                  <Prose body={section.body} className="mt-3 text-sm text-ink-600" />
                </section>
              ) : (
                <section className="py-5">
                  {section.title && <SectionHeading>{section.title}</SectionHeading>}
                  <Prose
                    body={section.kind === 'TERMS' ? section.body || proposal.termsText : section.body}
                    className={cn('mt-3', section.kind === 'TERMS' ? 'text-xs text-ink-600' : '')}
                  />
                </section>
              )}
            </Fragment>
          ))}

          {!hasTermsSection && proposal.termsText && (
            <section className="py-5">
              <SectionHeading>Terms</SectionHeading>
              <Prose body={proposal.termsText} className="mt-3 text-xs text-ink-600" />
            </section>
          )}
          {children}
        </div>
      </article>
    </div>
  )
}

export function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="border-b-2 border-[var(--p-brand)] pb-1.5 text-lg font-bold text-ink-900">{children}</h2>
  )
}

/** Who and where a proposal is for, as its cover lists them. */
export function coverDetails(
  proposal: Pick<ProposalView, 'customerName' | 'siteAddress' | 'jobNumber' | 'validUntil'>,
): CoverDetail[] {
  return [
    { label: 'Prepared for', value: proposal.customerName },
    { label: 'Property', value: proposal.siteAddress },
    { label: 'Job', value: proposal.jobNumber || null },
    { label: 'Valid until', value: proposal.validUntil ? formatDate(proposal.validUntil) : null },
  ]
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold tracking-[0.12em] text-[var(--p-accent-text)] uppercase">{label}</dt>
      <dd className="mt-0.5 font-semibold break-words whitespace-pre-line text-ink-900">{value}</dd>
    </div>
  )
}

/** A photo block's photos, three to a row, and a row per PDF. Each opens in a new tab. */
export function ProposalFiles({ files, className }: { files: ProposalFileView[]; className?: string | undefined }) {
  const photos = files.filter((file) => file.isImage)
  const documents = files.filter((file) => !file.isImage)
  if (files.length === 0) return null

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {photos.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {photos.map((file) => (
            <li key={file.id}>
              <a href={file.url} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={file.url}
                  alt={file.filename}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-xl object-cover ring-1 ring-ink-200 transition hover:opacity-90"
                />
              </a>
            </li>
          ))}
        </ul>
      )}
      {documents.length > 0 && (
        <ul className="flex flex-col gap-2">
          {documents.map((file) => (
            <li key={file.id}>
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 ring-1 ring-ink-200 transition hover:ring-[var(--p-brand)]"
              >
                <FileBadge />
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink-800">{file.filename}</span>
                <Icon name="download" className="size-4 text-ink-400" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The red "PDF" tile a file row leads with. */
export function FileBadge() {
  return (
    <span
      aria-hidden
      className="grid h-10 w-8 shrink-0 place-items-end justify-center rounded-md bg-white pb-1 text-[8px] font-extrabold text-white ring-1 ring-ink-200"
    >
      <span className="rounded-sm bg-red-600 px-1 py-px">PDF</span>
    </span>
  )
}

/** Paragraphs and bullets, from plain text. Never markup. */
export function Prose({ body, className }: { body: string | null | undefined; className?: string }) {
  const blocks = proposalBlocks(body)
  if (blocks.length === 0) return null

  return (
    <div className={cn('space-y-3 leading-relaxed', className)}>
      {blocks.map((block, index) =>
        block.kind === 'paragraph' ? (
          <p key={index} className="whitespace-pre-line">
            {block.text}
          </p>
        ) : (
          <ul key={index} className="space-y-1.5 pl-1">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex} className="flex gap-2.5">
                <span aria-hidden className="mt-[0.6em] size-1.5 shrink-0 rounded-full bg-[var(--p-brand)]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        ),
      )}
    </div>
  )
}

export function ProposalPricing({
  title,
  body,
  proposal,
  choice,
  onChoice,
  bare = false,
  edit,
}: {
  title: string | null
  body: string | null
  proposal: ProposalView
  choice: Choice
  onChoice?: ((choice: Choice) => void) | undefined
  /** The builder draws its own editable heading and words above the price. */
  bare?: boolean
  /**
   * The builder's editable tables, drawn where the line items and the extras
   * print. `included` is null where there are packages and no line items.
   */
  edit?: { included: ReactNode; extras: ReactNode } | undefined
}) {
  const hasPackages = proposal.options.length > 0
  const totals = totalsFor(proposal, choice)
  const chosenOption = totals.chosen.optionId
  const included = proposal.lines.filter((line) => !line.optionId && !line.isOptional)
  const addOns = availableAddOns(proposal.lines, chosenOption)
  const ticked = new Set(choice.addOnIds)

  const pick = (optionId: string) => {
    if (!onChoice) return
    // An add-on offered only with the old package goes with it.
    const offered = new Set(availableAddOns(proposal.lines, optionId).map((line) => line.id))
    onChoice({ optionId, addOnIds: choice.addOnIds.filter((id) => offered.has(id)) })
  }

  const toggle = (lineId: string) => {
    if (!onChoice) return
    onChoice({
      optionId: choice.optionId ?? chosenOption,
      addOnIds: ticked.has(lineId) ? choice.addOnIds.filter((id) => id !== lineId) : [...choice.addOnIds, lineId],
    })
  }

  return (
    <section aria-label={bare ? 'Price preview' : 'Pricing'} className={bare ? undefined : 'py-5'}>
      {bare ? null : (
        <>
          <SectionHeading>{title || (hasPackages ? 'Your options' : 'The work')}</SectionHeading>
          <Prose body={body} className="mt-3" />
        </>
      )}

      {(edit ? edit.included !== null : included.length > 0) && (
        <div className="mt-4">
          {hasPackages && <Kicker>Included in every option</Kicker>}
          {edit ? edit.included : <PriceTable lines={included} />}
        </div>
      )}

      {hasPackages && (
        <div
          role={onChoice ? 'radiogroup' : undefined}
          aria-label="Options"
          className={cn('mt-6 grid gap-4', proposal.options.length > 1 && 'sm:grid-cols-2')}
        >
          {proposal.options.map((option) => {
            const selected = option.id === chosenOption
            const price = totals.packages.find((entry) => entry.optionId === option.id)
            const lines = proposal.lines.filter((line) => line.optionId === option.id && !line.isOptional)
            const Tag = onChoice ? 'button' : 'div'

            return (
              <Tag
                key={option.id}
                data-option={option.id}
                {...(onChoice
                  ? {
                      type: 'button' as const,
                      role: 'radio',
                      'aria-checked': selected,
                      onClick: () => pick(option.id),
                    }
                  : {})}
                className={cn(
                  'relative flex flex-col rounded-2xl p-5 text-left transition-all',
                  selected
                    ? 'bg-[var(--p-brand-soft)] shadow-md ring-2 ring-[var(--p-brand)]'
                    : 'bg-white ring-1 ring-ink-200',
                  onChoice && !selected && 'hover:-translate-y-0.5 hover:shadow-md hover:ring-ink-300',
                  option.isRecommended && 'mt-3 sm:mt-0',
                )}
              >
                {option.isRecommended && (
                  <span className="absolute -top-3 left-5 rounded-full bg-[var(--p-brand)] px-3 py-1 text-[10px] font-bold tracking-[0.12em] text-[var(--p-on-brand)] uppercase shadow-sm">
                    Recommended
                  </span>
                )}
                <span className="flex items-start justify-between gap-3">
                  <span className="text-lg font-bold text-ink-900">{option.name}</span>
                  <span
                    aria-hidden
                    className={cn(
                      'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full transition-colors',
                      selected
                        ? 'bg-[var(--p-brand)] text-[var(--p-on-brand)]'
                        : 'ring-2 ring-ink-300',
                    )}
                  >
                    {selected && <Icon name="check" className="size-4" />}
                  </span>
                </span>
                {option.summary && <Prose body={option.summary} className="mt-2 text-sm text-ink-600" />}
                {lines.length > 0 && <LineList lines={lines} compact />}
                <span className="mt-auto pt-4 text-2xl font-extrabold text-ink-900 tabular-nums">
                  {money(price?.totalIncTax ?? 0)}
                  <span className="ml-1.5 text-xs font-normal text-ink-500">inc GST</span>
                </span>
              </Tag>
            )
          })}
        </div>
      )}

      {(edit || addOns.length > 0) && (
        <fieldset className="mt-7">
          <legend className="text-[11px] font-bold tracking-[0.14em] text-[var(--p-accent-text)] uppercase">
            Optional extras
          </legend>
          <p className="mt-1 text-sm text-ink-600">
            {onChoice ? 'Tick any you would like added. They are not in the price until ticked.' : 'Not in the price unless ticked.'}
          </p>
          {edit ? edit.extras : <PriceTable lines={addOns} ticked={ticked} onToggle={onChoice ? toggle : undefined} selectable />}
        </fieldset>
      )}

      <dl className="mt-7 ml-auto max-w-sm space-y-1 rounded-2xl bg-[var(--p-brand-soft)] px-5 py-4 text-sm">
        {proposal.discountAmount > 0 && (
          <Row label="Discount" value={`−${money(proposal.discountAmount)}`} />
        )}
        <Row label="Subtotal (ex GST)" value={money(totals.chosen.subtotalExTax)} />
        <Row label="GST" value={money(totals.chosen.totalTax)} />
        <div className="flex items-baseline justify-between border-t border-[var(--p-brand)]/40 pt-2 text-lg font-extrabold text-[var(--p-brand-text)]">
          <dt>{hasPackages || addOns.length > 0 ? 'Your total' : 'Total'}</dt>
          <dd className="tabular-nums">{money(totals.chosen.totalIncTax)}</dd>
        </div>
      </dl>
    </section>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between text-ink-600">
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  )
}

function Kicker({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-bold tracking-[0.14em] text-[var(--p-accent-text)] uppercase">{children}</p>
}

/**
 * Priced lines as a table: item, quantity, price and total, all inc GST. With
 * `selectable` each row is an extra with a tick box in front, and the whole
 * row is the label, so a thumb anywhere on it ticks it. On a phone the
 * quantity and price fold under the words.
 */
function PriceTable({
  lines,
  selectable = false,
  ticked,
  onToggle,
}: {
  lines: ProposalLineView[]
  selectable?: boolean
  ticked?: ReadonlySet<string>
  onToggle?: ((lineId: string) => void) | undefined
}) {
  const columns = selectable
    ? 'grid-cols-[1.5rem_minmax(0,1fr)_auto] sm:grid-cols-[1.5rem_minmax(0,1fr)_3.5rem_6.5rem_6.5rem]'
    : 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_3.5rem_6.5rem_6.5rem]'

  return (
    <div className="mt-3 overflow-hidden rounded-xl bg-white ring-1 ring-ink-200">
      <div
        aria-hidden
        className={cn(
          'hidden gap-x-3 bg-[var(--p-brand-soft)] px-4 py-2 text-[11px] font-bold tracking-[0.08em] text-[var(--p-accent-text)] uppercase sm:grid',
          columns,
        )}
      >
        {selectable && <span />}
        <span>Item</span>
        <span className="text-right">Qty</span>
        <span className="text-right">Price inc GST</span>
        <span className="text-right">Total inc GST</span>
      </div>
      <ul className="divide-y divide-ink-100">
        {lines.map((line) => {
          const on = ticked?.has(line.id) ?? false
          const unit = unitPriceIncTax(line)
          const cells = (
            <>
              <span className="min-w-0 whitespace-pre-line text-ink-800">
                {line.description}
                <span className="mt-0.5 block text-xs text-ink-500 tabular-nums sm:hidden">
                  {Number(line.quantity) === 1 ? money(unit) : `${line.quantity} × ${money(unit)}`}
                </span>
              </span>
              <span className="hidden text-right text-ink-600 tabular-nums sm:block">{line.quantity}</span>
              <span className="hidden text-right text-ink-600 tabular-nums sm:block">{money(unit)}</span>
              <span className="text-right font-semibold text-ink-900 tabular-nums">{money(line.lineTotalIncTax)}</span>
            </>
          )
          const row = cn('grid items-start gap-x-3 px-4 py-3 text-sm', columns)

          if (!selectable) {
            return (
              <li key={line.id} className={row}>
                {cells}
              </li>
            )
          }

          return (
            <li key={line.id}>
              <label
                className={cn(
                  row,
                  'relative transition-colors',
                  on && 'bg-[var(--p-brand-soft)]',
                  onToggle ? 'cursor-pointer hover:bg-[var(--p-brand-soft)]/60' : 'cursor-default',
                )}
              >
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={on}
                  disabled={!onToggle}
                  onChange={() => onToggle?.(line.id)}
                />
                <span
                  aria-hidden
                  className={cn(
                    'grid size-5 place-items-center rounded-md transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--p-brand)]',
                    on
                      ? 'bg-[var(--p-brand)] text-[var(--p-on-brand)]'
                      : 'bg-white ring-2 ring-ink-300 ring-inset',
                  )}
                >
                  {on && <Icon name="check" className="size-3.5" strokeWidth={3} />}
                </span>
                {cells}
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function quantityNote(quantity: string): string {
  const value = Number(quantity)
  return Number.isFinite(value) && value !== 1 ? ` × ${value}` : ''
}

function LineList({ lines, compact }: { lines: ProposalLineView[]; compact?: boolean }) {
  return (
    <ul className={cn('text-sm', compact ? 'mt-3 space-y-1.5 text-ink-700' : 'mt-2 divide-y divide-ink-100')}>
      {lines.map((line) => (
        <li key={line.id} className={cn('flex items-start gap-3', !compact && 'py-2.5')}>
          {compact && <Icon name="check" className="mt-0.5 size-4 shrink-0 text-[var(--p-brand-text)]" />}
          <span className="min-w-0 flex-1 whitespace-pre-line">
            {line.description}
            {quantityNote(line.quantity)}
          </span>
          {!compact && <span className="shrink-0 text-ink-900 tabular-nums">{money(line.lineTotalIncTax)}</span>}
        </li>
      ))}
    </ul>
  )
}
